import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Youtube, Instagram, Facebook, Music2, RefreshCw, Unplug, Loader2, CheckCircle2, AlertTriangle, Wrench } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_ICONS = { youtube: Youtube, instagram: Instagram, facebook: Facebook, tiktok: Music2 };
const PLATFORM_COLORS = { youtube: 'text-red-400', instagram: 'text-pink-400', facebook: 'text-blue-400', tiktok: 'text-cyan-300' };

const ERROR_MESSAGES = {
  invalid_state: 'Connection request could not be verified. Please try again.',
  expired_state: 'The connection request expired. Please try again.',
  token_exchange_failed: 'The platform rejected the connection. Please try again.',
  not_configured: 'This platform is not set up yet.',
  no_pages_found: 'No Facebook Pages were found on that account.',
  no_instagram_professional_account: 'No Instagram Professional account is linked to your Facebook Pages.',
  missing_params: 'The platform did not complete the connection.',
  access_denied: 'You declined the connection request.',
};

function PlatformRow({ p, scope, token, onChanged }) {
  const [busy, setBusy] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const conn = p.connection;
  const Icon = PLATFORM_ICONS[p.platform] || Youtube;
  const scopeParam = scope === 'system' ? '?account_scope=system' : '';

  const connect = async () => {
    setBusy('connect');
    try {
      const r = await axios.get(`${API}/api/social/connect/${p.platform}${scopeParam}`, { headers });
      window.location.assign(r.data.oauth_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not start ${p.name} connection`);
      setBusy(null);
    }
  };

  const disconnect = async () => {
    setBusy('disconnect');
    try {
      await axios.delete(`${API}/api/social/disconnect/${p.platform}${scopeParam}`, { headers });
      toast.success(`${p.name} disconnected`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not disconnect');
    } finally { setBusy(null); }
  };

  const refresh = async () => {
    setBusy('refresh');
    try {
      const r = await axios.post(`${API}/api/social/refresh/${p.platform}${scopeParam}`, {}, { headers });
      if (r.data.success) toast.success('Connection refreshed');
      else toast.warning(r.data.message || 'Please reconnect this account');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not refresh');
    } finally { setBusy(null); }
  };

  const hasIssue = conn && (conn.needs_reconnect || conn.status === 'reconnect_required');

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl bg-zinc-900/60 border border-zinc-800" data-testid={`social-platform-${scope}-${p.platform}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Icon className={`w-5 h-5 shrink-0 ${PLATFORM_COLORS[p.platform]}`} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white">{p.name}</span>
            {p.connected && !hasIssue && (
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" data-testid={`social-status-${scope}-${p.platform}`}>
                <CheckCircle2 className="w-3 h-3" /> Connected
              </span>
            )}
            {p.connected && hasIssue && (
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20" data-testid={`social-status-${scope}-${p.platform}`}>
                <AlertTriangle className="w-3 h-3" /> Needs attention
              </span>
            )}
            {!p.connected && !p.configured && (
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20" data-testid={`social-status-${scope}-${p.platform}`}>
                <Wrench className="w-3 h-3" /> Coming soon
              </span>
            )}
            {!p.connected && p.configured && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700" data-testid={`social-status-${scope}-${p.platform}`}>
                Not connected
              </span>
            )}
          </div>
          {p.connected && conn?.platform_username && (
            <p className="text-xs text-zinc-400 truncate mt-0.5" data-testid={`social-account-name-${scope}-${p.platform}`}>{conn.platform_username}</p>
          )}
          {p.connected && conn?.last_error && (
            <p className="text-xs text-red-400 mt-0.5">{conn.last_error}</p>
          )}
          {!p.connected && !p.configured && p.pending_reason && (
            <p className="text-xs text-zinc-500 mt-0.5">{p.pending_reason}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!p.connected && p.configured && (
          <Button size="sm" onClick={connect} disabled={busy !== null}
                  className="bg-orange-600 hover:bg-orange-500 text-white"
                  data-testid={`social-connect-${scope}-${p.platform}`}>
            {busy === 'connect' ? <Loader2 className="w-4 h-4 animate-spin" /> : `Connect ${p.name}`}
          </Button>
        )}
        {p.connected && (
          <>
            {hasIssue && p.configured && (
              <Button size="sm" onClick={connect} disabled={busy !== null}
                      className="bg-orange-600 hover:bg-orange-500 text-white"
                      data-testid={`social-reconnect-${scope}-${p.platform}`}>
                Reconnect
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={refresh} disabled={busy !== null}
                    className="border-zinc-700 text-zinc-300"
                    data-testid={`social-refresh-${scope}-${p.platform}`}>
              {busy === 'refresh' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
            <Button size="sm" variant="outline" onClick={disconnect} disabled={busy !== null}
                    className="border-zinc-700 text-zinc-400 hover:text-red-400"
                    data-testid={`social-disconnect-${scope}-${p.platform}`}>
              {busy === 'disconnect' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unplug className="w-4 h-4" />}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export const SocialAccountsPanel = ({ token, isFounder }) => {
  const [platforms, setPlatforms] = useState(null);
  const [systemPlatforms, setSystemPlatforms] = useState(null);

  const load = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const r = await axios.get(`${API}/api/social/platforms`, { headers });
      setPlatforms(r.data.platforms);
    } catch { /* transient */ }
    if (isFounder) {
      try {
        const r = await axios.get(`${API}/api/social/platforms?account_scope=system`, { headers });
        setSystemPlatforms(r.data.platforms);
      } catch { /* transient */ }
    }
  }, [token, isFounder]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('social_connected');
    const error = params.get('social_error');
    if (connected) {
      const channel = params.get('channel');
      toast.success(`${connected.charAt(0).toUpperCase() + connected.slice(1)} connected${channel ? ` — ${channel}` : ''}`);
    } else if (error) {
      toast.error(ERROR_MESSAGES[error] || `Connection failed (${error})`);
    }
    if (connected || error) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  if (!platforms) {
    return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  }

  return (
    <div className="space-y-3" data-testid="social-accounts-panel">
      <p className="text-xs text-zinc-500">
        Connect your channels so Delphi can publish content for you. You stay in control — nothing is posted without your permission settings.
      </p>
      {platforms.map((p) => (
        <PlatformRow key={p.platform} p={p} scope="user" token={token} onChanged={load} />
      ))}

      {isFounder && systemPlatforms && (
        <div className="pt-4 mt-4 border-t border-zinc-800" data-testid="social-system-accounts-section">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-orange-400">Delphi AI System Accounts</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/20">delphi-ai-org · founder only</span>
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            Accounts Delphi uses to promote itself. Kept separate from your personal channels.
          </p>
          <div className="space-y-3">
            {systemPlatforms.map((p) => (
              <PlatformRow key={p.platform} p={p} scope="system" token={token} onChanged={load} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
